import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { PasswordInput } from "../components/PasswordInput";

type State = "verifying" | "verified" | "error";

/**
 * Email-verification landing page.
 *
 * Reads the one-time token from the URL and confirms it on mount. The three
 * terminal states -- verified, invalid, expired/already-used -- are distinct,
 * and a resend form lets the owner recover without support.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<State>(token ? "verifying" : "error");
  const [message, setMessage] = useState(
    token ? "" : "This link is missing its token. Request a new verification email.",
  );
  const [email, setEmail] = useState("");
  const [resent, setResent] = useState(false);
  const [sending, setSending] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    api
      .verifyEmail(token)
      .then(() => setState("verified"))
      .catch((error) => {
        setState("error");
        setMessage(
          error instanceof ApiError
            ? error.fields.token?.join(" ") || error.message
            : "We could not verify this link. Please request a new one.",
        );
      });
  }, [token]);

  async function onResend(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    setSending(true);
    try {
      if (email.trim()) await api.resendVerification(email.trim());
    } catch {
      // Always the same outcome, to avoid account enumeration.
    } finally {
      setSending(false);
      setResent(true);
    }
  }

  return (
    <AuthCard title="Verify your email">
      {state === "verifying" ? <p className="text-sm text-slate-500">Confirming your link…</p> : null}

      {state === "verified" ? (
        <>
          <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Your email is verified. You are all set.
          </p>
          <Link
            to="/"
            className="mt-4 inline-block w-full rounded-md bg-slate-900 px-3 py-2.5 text-center text-sm font-medium text-white hover:bg-slate-800"
          >
            Continue to SBM One
          </Link>
        </>
      ) : null}

      {state === "error" ? (
        <>
          <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {message}
          </p>

          {resent ? (
            <p className="mt-4 rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600">
              If that account exists, we&apos;ve sent a new verification link. Check your inbox.
            </p>
          ) : (
            <form onSubmit={onResend} className="mt-4 space-y-3">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                Your email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              />
              <button
                type="submit"
                disabled={sending}
                className="w-full rounded-md bg-slate-900 px-3 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send a new link"}
              </button>
            </form>
          )}
        </>
      ) : null}

      <p className="mt-6 text-sm text-slate-500">
        <Link to="/signin" className="font-medium text-slate-900 hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthCard>
  );
}

export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(Boolean(token));
  const [validationError, setValidationError] = useState<string | null>(
    token ? null : "This invitation link is invalid or incomplete. Ask your administrator to resend it.",
  );
  const [inviteUser, setInviteUser] = useState<{
    email: string;
    first_name: string;
    last_name: string;
  } | null>(null);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    setValidating(true);
    setValidationError(null);

    api
      .validateInviteToken(token)
      .then((data) => {
        if (!mounted) return;
        setInviteUser({
          email: data.email,
          first_name: data.first_name,
          last_name: data.last_name,
        });
        setValidating(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setValidating(false);
        const msg =
          err instanceof ApiError
            ? err.fields.token?.join(" ") || err.message
            : "This invitation link has been revoked or expired. Please ask your administrator to resend it.";
        setValidationError(msg);
      });

    return () => {
      mounted = false;
    };
  }, [token]);

  if (!token || validationError) {
    return (
      <AuthCard title="Accept your invitation">
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {validationError || "This invitation link is invalid or incomplete. Ask your administrator to resend it."}
        </p>
        <p className="mt-6 text-sm text-slate-500">
          <Link to="/signin" className="font-medium text-slate-900 hover:underline">
            Back to sign in
          </Link>
        </p>
      </AuthCard>
    );
  }

  if (validating) {
    return (
      <AuthCard title="Accept your invitation">
        <p className="text-sm text-slate-500">Validating your invitation link…</p>
      </AuthCard>
    );
  }

  async function onSubmit(formEvent: React.FormEvent) {
    formEvent.preventDefault();
    if (password !== confirm) {
      setError("The passwords do not match.");
      return;
    }
    if (password.length < 10 || /^\d+$/.test(password) || /^\p{L}+$/u.test(password)) {
      setError("Use a mix of letters and numbers, at least 10 characters.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await acceptInvite({ token, password });
      navigate("/", { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.fields.token?.join(" ") || caught.message
          : "Could not reach the server.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard title="Create your password">
      <p className="mb-2 text-sm text-slate-600">
        Welcome{inviteUser?.first_name ? `, ${inviteUser.first_name}` : ""}! Complete your registration by creating your password.
      </p>
      {inviteUser?.email ? (
        <p className="mb-4 text-xs font-medium text-slate-500">
          Account: <span className="text-slate-800">{inviteUser.email}</span>
        </p>
      ) : null}
      <form onSubmit={onSubmit} className="space-y-3">
        <label htmlFor="password" className="block text-sm font-medium text-slate-700">
          New password
        </label>
        <PasswordInput
          id="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={setPassword}
        />
        <p className="text-xs text-slate-400">At least 10 characters, with letters and numbers.</p>
        <label htmlFor="confirm" className="block text-sm font-medium text-slate-700">
          Confirm password
        </label>
        <PasswordInput
          id="confirm"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={setConfirm}
        />
        {error ? (
          <p role="alert" className="text-sm text-rose-600">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-slate-900 px-3 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {submitting ? "Activating account…" : "Complete registration & sign in"}
        </button>
      </form>
      <p className="mt-6 text-sm text-slate-500">
        <Link to="/signin" className="font-medium text-slate-900 hover:underline">
          Back to sign in
        </Link>
      </p>
    </AuthCard>
  );
}

export function AuthCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-slate-50 px-6 py-12">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-xs">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex size-8 items-center justify-center rounded-md bg-slate-900 text-sm font-semibold text-white">
            S
          </div>
          <span className="text-sm font-semibold text-slate-900">SBM One</span>
        </div>
        <h1 className="mb-4 text-xl font-semibold tracking-tight text-slate-900">{title}</h1>
        {children}
      </div>
    </div>
  );
}
